// What this solution may do (02-DESIGN/14-solutions/01-the-manifest.md § the
// manifest as the consent surface), counted first
// (02-DESIGN/03-console/01-the-console.md § conventions: density and length;
// the moves, page by page: a solution's page): one line of who published
// which revision; one row per workload, its role and its permissions as
// counts, with the image's name and its digest shortened with copy; the row
// opens the role's page, `solutions/<solution>/roles/<role>`, with the full
// lists, the platform's words under a disclosure; then what it requires of
// the workspace, counted, each count opening its list in place, under the
// line; then its panels and its own page, one line each (hq decision 0069).
// Where it is installed, each role's workload and each store are links to
// their pages, where the role is offered them (§ conventions: a name is a
// link). The
// same rendering the admin consents to at *Install* and reads later on the
// solution's *What it may do* tab, so consent is reviewable.

import { useState, type ReactNode } from "react";

import { tenantPath } from "../../address/address.ts";
import { pageOffered } from "../../roles/offers.ts";
import { bytes } from "../../ui/components.tsx";
import { Ident, NameLink } from "../../ui/ident.tsx";
import { Table } from "../../ui/table.tsx";
import type { Manifest, Workload } from "../../wire/solutions.ts";
import { useSurface } from "../surface.tsx";
import type { Ids } from "./panel.ts";
import {
  capabilityWords,
  countedLine,
  digestOf,
  mayDo,
  publishedLine,
  requirements,
  scopeWords,
  tenantSecrets,
  type RequirementKind,
} from "./words.ts";

export interface Provenance {
  publisher: string;
  revision: number;
  /** The reference pinned to its digest, per role, where the catalogue names it. */
  images?: Record<string, string>;
}

/** rolePath is the page of one role a solution's declaration names: `solutions/<solution>/roles/<role>`. */
export function rolePath(tenant: string, solution: string, role: string): string {
  return tenantPath(tenant, "solutions", solution, "roles", role);
}

/** storePath is the page of a store a solution declares, as the install creates it: `stores/<solution>-<store>`. */
export function storePath(tenant: string, solution: string, store: string): string {
  return tenantPath(tenant, "stores", `${solution}-${store}`);
}

export function Declaration({
  m,
  from,
  ids,
}: {
  m: Manifest;
  from: Provenance;
  /** The workload id per role, where the solution is installed here. */
  ids?: Ids;
}) {
  const s = useSurface();
  const [list, setList] = useState<RequirementKind | undefined>(undefined);
  const workloads = m.workloads ?? [];
  const needs = requirements(m);
  const workloadTo = (id: string) =>
    pageOffered(s.role, "workloads") ? tenantPath(s.tenant, "workloads", id) : undefined;
  const placed = ids !== undefined && Object.keys(ids).length > 0;
  return (
    <div className="declaration stack">
      <p className="small muted">{publishedLine(from.publisher, from.revision, m.version)}</p>

      {workloads.length > 0 && (
        <Table
          label="Workloads"
          rows={workloads}
          rowKey={(w) => w.role}
          sort={{ key: "role", dir: "asc" }}
          to={(w) => rolePath(s.tenant, m.name, w.role)}
          columns={[
            {
              key: "role",
              header: "Workload",
              width: "minmax(0, 3fr)",
              value: (w) => w.role,
              text: (w) => `${w.role} ${countedLine(w)}`,
              cell: (w) => (
                <span className="cell">
                  <span className="cell-main">{w.role}</span>
                  <span className="cell-sub">{countedLine(w)}</span>
                </span>
              ),
            },
            {
              key: "image",
              header: "Image",
              width: "minmax(0, 2fr)",
              narrow: false,
              value: (w) => w.image,
              cell: (w) => <Image w={w} pinned={from.images?.[w.role]} />,
            },
            ...(placed
              ? [
                  {
                    key: "placed",
                    header: "Runs here as",
                    width: "minmax(0, 1fr)",
                    narrow: false,
                    value: (w: Workload) => ids[w.role] ?? "",
                    cell: (w: Workload) => {
                      const id = ids[w.role];
                      return id === undefined ? (
                        <span className="faint">not placed</span>
                      ) : (
                        <Ident value={id} label="id" to={workloadTo(id)} />
                      );
                    },
                  },
                ]
              : []),
          ]}
        />
      )}

      {needs.length > 0 && (
        <section className="section">
          <div className="section-head">
            <h2>What it requires of the workspace</h2>
          </div>
          <p>
            {needs.map((x, i) => (
              <span key={x.kind}>
                {i > 0 && ", "}
                <button
                  type="button"
                  className="text-link"
                  aria-expanded={list === x.kind}
                  onClick={() => {
                    setList(list === x.kind ? undefined : x.kind);
                  }}
                >
                  {x.words}
                </button>
              </span>
            ))}
          </p>
          {list !== undefined && (
            <RequirementList
              m={m}
              kind={list}
              storeTo={
                placed && pageOffered(s.role, "stores")
                  ? (store) => storePath(s.tenant, m.name, store)
                  : undefined
              }
            />
          )}
        </section>
      )}

      {((m.panels ?? []).length > 0 || m.page !== undefined) && (
        <dl className="kv small">
          {(m.panels ?? []).map((p) => (
            <Row key={`p-${p.name}`} label={p.title}>
              a page drawn by the console under Solutions
            </Row>
          ))}
          {m.page !== undefined && (
            <Row label={m.page.label}>
              its own page, served by its publisher from <span className="mono">{m.page.origin}</span>: it
              runs as the person using it, for minutes at a time, and reaches only what is listed here — what
              you see and type there, its publisher sees
            </Row>
          )}
        </dl>
      )}
    </div>
  );
}

/** Image is the image's name, and under it the digest it is pinned to, shortened with copy. */
function Image({ w, pinned }: { w: Workload; pinned?: string }) {
  const digest = digestOf(pinned);
  return (
    <span className="cell">
      <span className="cell-main mono">{w.image}</span>
      {digest !== undefined && (
        <span className="cell-sub">
          <Ident value={digest} label="digest" />
        </span>
      )}
    </span>
  );
}

/**
 * WorkloadLists is one workload's full lists, in the person's words, the
 * platform's under a disclosure: the role's page. Where the solution is
 * installed, `placed` is the workload the role runs as here, a link to its
 * page where the role is offered it.
 */
export function WorkloadLists({
  w,
  pinned,
  placed,
}: {
  w: Workload;
  pinned?: string;
  placed?: { id: string; to?: string };
}) {
  const can = mayDo(w);
  const digest = digestOf(pinned);
  const admin = (w.serves ?? []).filter((v) => v.admin === true);
  return (
    <div className="stack">
      {w.description !== undefined && <p className="small">{w.description}</p>}
      {w.on_grant === true && (
        <p className="small faint">runs only while another workspace sends to this one</p>
      )}
      <dl className="kv">
        {placed !== undefined && (
          <Row label="Runs here as">
            <Ident value={placed.id} label="id" to={placed.to} />
          </Row>
        )}
        <Row label="Image">
          <span className="mono">{w.image}</span>
          {digest !== undefined && (
            <div className="small">
              <Ident value={digest} label="digest" />
            </div>
          )}
        </Row>
        <Row label="May">
          {can.length === 0 ? (
            <span className="faint">nothing beyond its own settings, secrets and stores</span>
          ) : (
            <ul className="plain">
              {can.map((c) => (
                <li key={c.words}>{c.words}</li>
              ))}
            </ul>
          )}
          {can.length > 0 && (
            <details className="small">
              <summary>In the platform's words</summary>
              {can.flatMap((c) =>
                c.patterns.map((p) => (
                  <div className="mono faint" key={p}>
                    {p}
                  </div>
                )),
              )}
            </details>
          )}
        </Row>
        {((w.sends ?? []).length > 0 || (w.receives ?? []).length > 0) && (
          <Row label="Across workspaces">
            {(w.sends ?? []).map((c) => (
              <div key={`s-${c}`}>{capabilityWords(c, "sends")}</div>
            ))}
            {(w.receives ?? []).map((c) => (
              <div key={`r-${c}`}>{capabilityWords(c, "receives")}</div>
            ))}
          </Row>
        )}
        {w.controller !== undefined && (
          <Row label="Instructed by">
            the workspace that holds the control grant, and nobody inside this one
            <ul className="plain">
              {(w.instructions ?? []).map((i) => (
                <li key={i.name}>
                  <span className="mono">{i.name}</span> — {i.consequence}
                </li>
              ))}
            </ul>
          </Row>
        )}
        {admin.length > 0 && (
          <Row label="Only an admin may">
            {admin.map((v) => (
              <div key={v.verb}>{v.description ?? v.verb}</div>
            ))}
          </Row>
        )}
        <Row label="Answers as">
          {(w.names ?? []).length === 0 ? (
            <span className="faint">no name</span>
          ) : (
            (w.names ?? []).map((n) => (
              <div className="mono" key={n}>
                {n}
              </div>
            ))
          )}
        </Row>
        <Row label="Needs">
          {w.resources.cpus} vCPU · {w.resources.memory_mib} MiB
          {(w.replicas ?? 0) > 1 && ` · ${String(w.replicas ?? 1)} copies`}
          {(w.requires ?? []).length > 0 && ` · a host with ${(w.requires ?? []).join(", ")}`}
        </Row>
        <Row label="May reach">
          {(w.egress ?? []).length === 0 ? (
            <span className="faint">nothing outside Impire</span>
          ) : (
            (w.egress ?? []).map((e) => (
              <div className="mono" key={e}>
                {e}
              </div>
            ))
          )}
        </Row>
      </dl>
    </div>
  );
}

/**
 * RequirementList is one kind of what the solution requires of the
 * workspace, listed; a store's name is a link to its page where `storeTo`
 * says where, which is only where it was installed here.
 */
function RequirementList({
  m,
  kind,
  storeTo,
}: {
  m: Manifest;
  kind: RequirementKind;
  storeTo?: (store: string) => string;
}) {
  return (
    <dl className="kv">
      {kind === "settings" &&
        (m.settings ?? [])
          .filter((s) => s.secret !== true || s.scope !== "tenant")
          .map((s) => (
            <Row key={s.name} label={s.label}>
              {scopeWords(s)}
            </Row>
          ))}
      {kind === "secrets" &&
        tenantSecrets(m).map((s) => (
          <Row key={s.name} label={s.name}>
            a secret an admin stores{s.required ? ", required" : ""}: {s.hint}
          </Row>
        ))}
      {kind === "stores" &&
        (m.stores ?? []).map((s) => (
          <Row
            key={s.name}
            label={
              storeTo !== undefined ? (
                <NameLink to={storeTo(s.name)}>{`${m.name}-${s.name}`}</NameLink>
              ) : (
                `${m.name}-${s.name}`
              )
            }
          >
            a store{s.history === "full" ? " that keeps all of its history" : ""}
            {s.budget !== undefined && s.budget > 0 ? `, up to ${bytes(s.budget)}` : ""}
          </Row>
        ))}
      {kind === "fixed" &&
        (m.required?.documents ?? []).map((d) => (
          <Row
            key={d.target}
            label={d.target === "solution" ? "its own settings" : `every ${d.target} workload`}
          >
            {Object.keys(d.document).join(", ")} fixed, and not changed by the workspace
          </Row>
        ))}
      {kind === "guardrails" &&
        (m.required?.rules ?? []).map((r) => (
          <Row key={r.id} label={r.id}>
            a guardrail rule the admins extend and do not remove
          </Row>
        ))}
    </dl>
  );
}

function Row({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}
